/**
 * Manga page script — parser & serializer for the plain-text screenplay format
 * writers use to write a page's panels and dialogue as ONE continuous
 * document (see the "Roteiro da página" editor). This is the round-trip
 * between prose and the structured panel/bubble data model:
 *
 *   parsePageScript()    prose  → { panels: [{ description, dialogues }] }
 *   serializePageScript() page   → prose
 *
 * Grammar (tolerant — accepts common variations, never throws):
 *
 *   QUADRO 1
 *   Rami no topo da mansão, plano aberto, cidade ao fundo à noite.
 *   Rami: Hoje eu termino isso.
 *   (pensamento) Rami: Se eu falhar… ninguém saberá.
 *
 *   QUADRO 2
 *   Close no rosto do Prefeito Cruz, sorriso frio.
 *   Cruz: Você chegou tarde demais.
 *   SFX: CRASH!
 *   Narrador: A cidade nunca dorme.
 *   (legenda) Cidade Alta, 23h.
 *
 * - New panel: a line that is ONLY a marker — optional "#", optional word
 *   (quadro/q/panel/page), optional digits (e.g. "QUADRO 1", "# Quadro 2",
 *   "Q3") — or a bare "---" line. If the text doesn't start with a marker,
 *   the WHOLE thing is panel 1.
 * - Dialogue: "Nome: texto" (name up to 40 chars, no ':' inside) → speech.
 *   Prefix modifiers change the type: (pensamento)/(thought) → thought,
 *   (grito)/(shout) → shout, (sussurro)/(whisper) → whisper,
 *   (narração)/(narracao)/(narration) → narration, (legenda)/(caption) →
 *   caption (no name — the rest of the line IS the caption text).
 *   Specials: "SFX:"/"EFEITO:" → sfx (giant onomatopoeia letreiro, no balloon
 *   body), speaker 'SFX'. "Narrador:"/"Narração:"/"Narration:" → narration.
 * - Everything else (non-empty, not a marker, not a dialogue line) is panel
 *   DESCRIPTION prose, joined with spaces in the order it appears.
 */
import type { BubbleType, MangaBubble, MangaPage, MangaPanel } from './api';

export interface ParsedDialogue {
  /** Character name, NARRATOR/SFX label, or '' when no name was given. */
  speaker: string;
  type: BubbleType;
  text: string;
}
export interface ParsedPanel {
  description: string;
  dialogues: ParsedDialogue[];
}
export interface ParsedPageScript {
  panels: ParsedPanel[];
}

// ── Sentinel speakers ────────────────────────────────────────────────────
// These are stored on the bubble (so it's `speaker`-truthy → "script
// managed", trackable for reconciliation/round-trip) even though the short
// text form doesn't carry an explicit name for them — the serializer always
// prints the FIXED literal for these types, ignoring whatever name is stored.
export const NARRATOR_SPEAKER = 'Narrador';
export const SFX_SPEAKER = 'SFX';
export const CAPTION_SPEAKER = 'CAPTION';

// A line that's ONLY a panel marker: optional "#", optional word
// (quadro/q/panel/page), optional space, then digits — or a bare "---".
const PANEL_HEADER_RE = /^(#\s*)?(?:quadro|q|panel|page)?\s*(\d+)\s*$/i;
const PANEL_DASH_RE = /^-{3,}$/;

// `SFX: texto` / `EFEITO: texto` → shout, speaker 'SFX'.
const SFX_RE = /^(?:sfx|efeito)\s*:\s*([\s\S]*)$/i;
// `Narrador:` / `Narração:` / `Narracao:` / `Narration:` → narration.
const NARRATOR_RE = /^(?:narrador|narra[cç][aã]o|narration)\s*:\s*([\s\S]*)$/i;
// Leading `(modificador)` before the rest of the line.
const MODIFIER_RE = /^\(\s*([^)]*)\s*\)\s*([\s\S]*)$/;
// `Nome: texto` — name up to 40 chars, no ':' inside.
const NAME_COLON_RE = /^([^:\n]{1,40}):\s*([\s\S]*)$/;

const MODIFIER_TYPES: Record<string, BubbleType> = {
  pensamento: 'thought', thought: 'thought',
  grito: 'shout', shout: 'shout',
  sussurro: 'whisper', whisper: 'whisper',
  narracao: 'narration', narration: 'narration',
  legenda: 'caption', caption: 'caption',
};

function normalizeWord(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();
}

/** Classifies ONE non-empty, non-header content line as a dialogue (with
 * type/speaker/text) or `null` when it's plain panel-description prose. */
function parseContentLine(raw: string): ParsedDialogue | null {
  const line = raw.trim();

  const sfx = line.match(SFX_RE);
  if (sfx) return { speaker: SFX_SPEAKER, type: 'sfx', text: sfx[1].trim() };

  const narr = line.match(NARRATOR_RE);
  if (narr) return { speaker: NARRATOR_SPEAKER, type: 'narration', text: narr[1].trim() };

  const mod = line.match(MODIFIER_RE);
  if (mod) {
    const type = MODIFIER_TYPES[normalizeWord(mod[1])];
    if (type) {
      const rest = mod[2].trim();
      if (type === 'caption') return { speaker: CAPTION_SPEAKER, type, text: rest };
      const named = rest.match(NAME_COLON_RE);
      if (named) return { speaker: named[1].trim(), type, text: named[2].trim() };
      return { speaker: '', type, text: rest }; // tolerant fallback — no name given
    }
  }

  const named = line.match(NAME_COLON_RE);
  if (named) return { speaker: named[1].trim(), type: 'speech', text: named[2].trim() };

  return null; // plain description prose
}

/** Parses the plain-text page script into panels with descriptions + ordered
 * dialogues. Tolerant: if the text doesn't start with a panel marker, the
 * WHOLE thing is treated as panel 1. Never throws. */
export function parsePageScript(text: string): ParsedPageScript {
  const lines = (text ?? '').split(/\r\n|\r|\n/);
  const panels: ParsedPanel[] = [];
  const descParts: string[][] = []; // parallel to `panels`, joined at the end
  let hasCurrent = false;

  const startPanel = () => {
    panels.push({ description: '', dialogues: [] });
    descParts.push([]);
    hasCurrent = true;
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (PANEL_HEADER_RE.test(line) || PANEL_DASH_RE.test(line)) {
      startPanel();
      continue;
    }
    if (!hasCurrent) startPanel(); // no marker yet — everything is panel 1
    const parsed = parseContentLine(line);
    if (parsed) panels[panels.length - 1].dialogues.push(parsed);
    else descParts[descParts.length - 1].push(line);
  }

  panels.forEach((p, i) => { p.description = descParts[i].join(' ').trim(); });
  return { panels };
}

// ── Serialize ────────────────────────────────────────────────────────────

function dialogueLine(speaker: string, type: BubbleType, text: string): string {
  switch (type) {
    case 'speech': return `${speaker}: ${text}`;
    case 'thought': return `(pensamento) ${speaker}: ${text}`;
    case 'shout': return speaker === SFX_SPEAKER ? `SFX: ${text}` : `(grito) ${speaker}: ${text}`;
    case 'whisper': return `(sussurro) ${speaker}: ${text}`;
    case 'narration': return `Narrador: ${text}`;
    case 'caption': return `(legenda) ${text}`;
    case 'sfx': return `SFX: ${text}`;
    default: return `${speaker}: ${text}`;
  }
}

function panelBounds(panel: MangaPanel) {
  const xs = panel.points.map((p) => p.x), ys = panel.points.map((p) => p.y);
  return { minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) };
}

/** Builds the raw content lines (description + dialogue, reading order) for
 * ONE panel — shared by `serializePageScript` (which prefixes a "QUADRO N"
 * marker) and `serializePanelBlock` (which doesn't — see its docstring).
 * Dialogue lines are derived from BUBBLES whose `speaker` is truthy and
 * whose center falls inside the panel's bounding box, NOT from
 * `panel.script`, which only stores the last-applied/generated description. */
function panelContentLines(page: MangaPage, panelIndex: number): string[] {
  const panel = page.layout.panels[panelIndex];
  if (!panel) return [];
  const { minX, maxX, minY, maxY } = panelBounds(panel);
  const inPanel = page.layout.bubbles
    .filter((b): b is MangaBubble & { speaker: string } => !!b.speaker)
    .filter((b) => {
      const cx = b.x + b.w / 2, cy = b.y + b.h / 2;
      return cx >= minX && cx <= maxX && cy >= minY && cy <= maxY;
    })
    .sort((a, b) => (a.y + a.h / 2) - (b.y + b.h / 2));
  const lines: string[] = [];
  const desc = panel.script?.description?.trim();
  if (desc) lines.push(desc);
  for (const b of inPanel) lines.push(dialogueLine(b.speaker, b.type, b.text));
  return lines;
}

/** Serializes a page back into the plain-text screenplay format — the
 * inverse of `parsePageScript`. One "QUADRO N" block per panel, in order. */
export function serializePageScript(page: MangaPage): string {
  const blocks = page.layout.panels.map((_, i) => [`QUADRO ${i + 1}`, ...panelContentLines(page, i)].join('\n'));
  return blocks.join('\n\n');
}

/** Serializes ONE panel's description + dialogue as a free-write block —
 * same content as its `serializePageScript` block, but WITHOUT the leading
 * "QUADRO N" marker, so it round-trips through `parsePageScript` as panel 0
 * (a marker-less document is entirely panel 1 — see its docstring), ready to
 * be shown/edited in a per-panel textarea. */
export function serializePanelBlock(page: MangaPage, panelIndex: number): string {
  return panelContentLines(page, panelIndex).join('\n');
}
